import { SimulationTrackerStatusMessage } from './../enums/simulation-tracker-status';
import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { StudyArea } from '../interfaces/study-area';
import {
  SimulationTracker,
  SimulationTrackerIds,
} from '../interfaces/simulation-tracker';
import { SimulationTrackerItem } from '../interfaces/simulation-tracker-item';
import {
  SimulationTrackerStatus,
} from '../enums/simulation-tracker-status';

type StatusCategory = 'in-progress' | 'done' | 'success' | 'failed';

@Injectable({
  providedIn: 'root',
})
export class SimulationTrackerDataService {
  /**
   * Combines study areas and simulation trackers, enriching each tracker with study area details.
   * @param studyAreas$ Observable stream of StudyAreas
   * @param simulationTrackers$ Observable stream of SimulationTrackers
   * @param expirationDays The number of days to retain the tracker before expiration
   * @returns Observable<SimulationTrackerItem[]> with combined data
   */
  combineStudyAreaAndSimulationTrackers(
    studyAreas$: Observable<StudyArea[]>,
    simulationTrackers$: Observable<SimulationTracker[]>,
    expirationDays: number
  ): Observable<SimulationTrackerItem[]> {
    return combineLatest([studyAreas$, simulationTrackers$]).pipe(
      map(
        ([studyAreas, simulationTrackers]: [
          StudyArea[],
          SimulationTracker[]
        ]) =>
          simulationTrackers.map((tracker: SimulationTracker) =>
            this.mapToSimulationTrackerItem(tracker, studyAreas, expirationDays)
          )
      )
    );
  }

  /**
   * Maps a SimulationTracker to a SimulationTrackerItem, enriching it with study area details and expiration date.
   * @param tracker A single SimulationTracker object
   * @param studyAreas An array of StudyArea objects
   * @param expirationDays The number of days until expiration
   * @returns A fully constructed SimulationTrackerItem object
   */
  private mapToSimulationTrackerItem(
    tracker: SimulationTracker,
    studyAreas: StudyArea[],
    expirationDays: number
  ): SimulationTrackerItem {
    const studyArea: StudyArea | undefined = studyAreas.find(
      (area: StudyArea) => area.id == tracker.studyAreaId
    );

    const createdDate: Date = tracker.created
      ? new Date(tracker.created)
      : new Date();

    const expirationDate: Date = tracker.statusUpdated
      ? new Date(tracker.statusUpdated)
      : new Date();

    expirationDate.setDate(expirationDate.getDate() + expirationDays);
    const tags: { theme: string; icon: string; value: string }[] =
      this.buildTags(tracker);
    return {
      simulationTrackerId: tracker.simulationTrackerId,
      simulationId: tracker.simulationId,
      simulationName: tracker.simulationName,
      studyAreaId: tracker.studyAreaId,
      studyAreaName: studyArea?.studyAreaName ?? 'Unknown', // Default if not found
      totalArea: this.formatArea(studyArea), // Correctly formatted area measurement + unit
      areaMeasurement: studyArea?.areaMeasurement ?? 0,
      terrainGridId: tracker.terrainGridId,
      userId: tracker.userId,
      userName: tracker.userName,
      organizationId: tracker.organizationId,
      statusMessage: this.getStatusLabel(tracker.status), // Ensure proper typing
      created: createdDate,
      expiration: expirationDate, // Expiration = Created Date + Expiration Days
      tags: tags, // Generate tags dynamically
      flatTags: tags.map((tag) => tag.value).join(' '),
      statusUpdated: tracker.statusUpdated ?? new Date(), // Default to current date if missing
      deleted: tracker.deleted,
      favorited: tracker.favorited ?? false, // Retain property from SimulationTracker
      selected: false, // Retain property from SimulationTracker
      hasAdminAccess: tracker.hasAdminAccess == true, // Default to false if not present
      shared: tracker.shared == true ? 'Yes' : '', // Default to false if not present
      scenario: tracker.scenario ? tracker.scenario : '', // Default to empty string if not present
      scenarioId: tracker.scenarioId ?? '', // Default to empty string if not present
    };
  }

  getSimulationTrackerDetails(
    simulationTracker: SimulationTracker
  ): { label: string; value: string | number }[] {
    const details: {
      label: string;
      tooltip?: string;
      value: string | number;
    }[] = [];

    if (simulationTracker.iterations !== undefined) {
      details.push({
        label: 'Loops',
        value: simulationTracker.iterations,
      });
    }

    if (simulationTracker.scenario && simulationTracker.scenario !== '') {
      details.push({
        label: 'Scenario',
        value: simulationTracker.scenario,
      });
    }

    return details;
  }

  private getStatusLabel(
    status?: SimulationTrackerStatus
  ): SimulationTrackerStatusMessage {
    if (status === null || status === undefined) {
      return SimulationTrackerStatusMessage.Pending;
    }

    return (
      SimulationTrackerDataService.SimulationTrackerStatusMap[status] ||
      SimulationTrackerStatusMessage.Pending
    );
  }

  /**
   * Builds an array of tags based on the simulation tracker properties.
   * @param tracker The SimulationTracker object
   * @returns An array of tag objects
   */
  private buildTags(
    tracker: SimulationTracker
  ): { theme: string; icon: string; value: string }[] {
    const tags: { theme: string; icon: string; value: string }[] = [];

    if (tracker.favorited) {
      tags.push({
        theme: 'gray',
        icon: 'symbol-star',
        value: 'Favorite',
      });
    }

    if (tracker.scenarioId) {
      tags.push({
        theme: 'gray',
        icon: '',
        value: `Scenario ${tracker.scenario}`,
      });
    }

    return tags;
  }



  /**
   * Formats the area measurement with one decimal place and appends the unit.
   * @param studyArea The StudyArea object
   * @returns A formatted string like "1234.5 sq km"
   */
  private formatArea(studyArea?: StudyArea): string {
    if (
      !studyArea ||
      studyArea.areaMeasurement === undefined ||
      !studyArea.areaUnit
    ) {
      return 'N/A'; // Handle missing values
    }

    return `${studyArea.areaMeasurement.toFixed(1)} ${studyArea.areaUnit}`;
  }

  /**
   * Gets the theme color for a given simulation status.
   * @param status The simulation status.
   * @returns The corresponding theme string.
   */
  static GetStatusTheme(status: SimulationTrackerStatusMessage): string {
    return this.SimulationTrackerStatusTheme[status];
  }

  /**
   * Maps simulation statuses to their corresponding theme colors.
   * @param status The simulation status.
   * @returns The corresponding theme string.
   */
  static SimulationTrackerStatusTheme: Record<
    SimulationTrackerStatusMessage,
    string
  > = {
      [SimulationTrackerStatusMessage.Pending]: 'inactive',
      [SimulationTrackerStatusMessage.Completed]: 'success',
      [SimulationTrackerStatusMessage.Failed]: 'error',
      [SimulationTrackerStatusMessage.Running]: 'info',
      [SimulationTrackerStatusMessage.GeneratingResults]: 'info',
      [SimulationTrackerStatusMessage.Unknown]: 'inactive',
    };

  /**
   * Maps simulation statuses to their corresponding labels for display.
   * @param status The simulation status.
   * @returns The corresponding label string.
   */
  static readonly MinifiedTrackerStatusMap: Record<
    SimulationTrackerStatus,
    string
  > = {
      [SimulationTrackerStatus.Pending]: 'In-Progress',
      [SimulationTrackerStatus.Running]: 'In-Progress',
      [SimulationTrackerStatus.GeneratingResults]: 'In-Progress',
      [SimulationTrackerStatus.Failed]: 'Failed',
      [SimulationTrackerStatus.Completed]: 'Complete',
    };

  /**
   * Maps simulation statuses to their corresponding color classes.
   * @param status The simulation status.
   * @returns The corresponding color class string.
   */
  static readonly MinifiedTrackerStatusColorMap: Record<
    SimulationTrackerStatus,
    string
  > = {
      [SimulationTrackerStatus.Pending]: 'basin-color-warning',
      [SimulationTrackerStatus.Running]: 'basin-color-warning',
      [SimulationTrackerStatus.GeneratingResults]: 'basin-color-warning',
      [SimulationTrackerStatus.Failed]: 'basin-color-danger',
      [SimulationTrackerStatus.Completed]: 'basin-color-success',
    };

  /**
   * Maps simulation statuses to their corresponding color classes.
   * @param status The simulation status.
   * @returns The corresponding color class string.
   */
  static readonly TrackerStatusIcon: Record<
    SimulationTrackerStatusMessage,
    string
  > = {
      [SimulationTrackerStatusMessage.Pending]: 'symbol-clock',
      [SimulationTrackerStatusMessage.Running]: 'symbol-sync',
      [SimulationTrackerStatusMessage.GeneratingResults]: 'symbol-sync',
      [SimulationTrackerStatusMessage.Failed]: 'symbol-error',
      [SimulationTrackerStatusMessage.Completed]: 'symbol-check',
      [SimulationTrackerStatusMessage.Unknown]: 'symbol-clock',
    };

  /**
   * Gets the icon for a given simulation status.
   * @param status The simulation status.
   * @returns The corresponding icon string.
   */
  static GetStatusIcon(status: SimulationTrackerStatusMessage): string {
    return this.TrackerStatusIcon[status];
  }

  /**
   * Maps simulation statuses to their corresponding labels for display.
   * @param status The simulation status.
   * @returns The corresponding label string.
   */
  static SimulationTrackerStatusMap: Record<
    SimulationTrackerStatus,
    SimulationTrackerStatusMessage
  > = {
      [SimulationTrackerStatus.Pending]: SimulationTrackerStatusMessage.Pending,
      [SimulationTrackerStatus.Completed]:
        SimulationTrackerStatusMessage.Completed,
      [SimulationTrackerStatus.Failed]: SimulationTrackerStatusMessage.Failed,
      [SimulationTrackerStatus.Running]: SimulationTrackerStatusMessage.Running,
      [SimulationTrackerStatus.GeneratingResults]:
        SimulationTrackerStatusMessage.GeneratingResults,
    };

  static SimulationTrackerStatusReverseMap: Record<SimulationTrackerStatusMessage, SimulationTrackerStatus> = SimulationTrackerDataService.CreateReverseMap();

  private static CreateReverseMap(): Record<SimulationTrackerStatusMessage, SimulationTrackerStatus> {
    return Object.fromEntries(
      Object.entries(SimulationTrackerDataService.SimulationTrackerStatusMap).map(([key, value]) => [value, Number(key)])
    ) as Record<SimulationTrackerStatusMessage, SimulationTrackerStatus>;
  }

  /**
   * Combines status mapping into a unified object for display purposes.
   * @param status The numeric SimulationTrackerStatus enum value.
   * @returns An object containing label, theme, colorClass, and icon.
   */
  public static GetStatusDisplay(message: SimulationTrackerStatusMessage): {
    label: string;
    theme: string;
    colorClass: string;
    icon: string;
  } {

    const colorClass: SimulationTrackerStatus = this.SimulationTrackerStatusReverseMap[message];
    return {
      label: message ?? 'Unknown',
      theme: this.GetStatusTheme(message) ?? 'inactive',
      colorClass: this.MinifiedTrackerStatusColorMap[colorClass] ?? 'basin-color-inactive',
      icon: this.TrackerStatusIcon[message] ?? 'symbol-help'
    };
  }

  /**
   * Formats the expiration countdown.
   * If expiration is in the future, show "Xd Xh".
   * If past, show "Expired".
   * @param expiration The expiration date.
   * @returns A formatted expiration string.
   */
  static FormatExpiration(expiration: Date | null | undefined): string {
    if (!expiration) return '';

    const now: Date = new Date();
    const diffMs: number = new Date(expiration).getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Expired';
    }

    const days: number = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours: number = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    return `${days}d ${hours}h`;
  }

  private static readonly _categoryMap: Record<
    SimulationTrackerStatus,
    StatusCategory
  > = {
      [SimulationTrackerStatus.Pending]: 'in-progress',
      [SimulationTrackerStatus.Running]: 'in-progress',
      [SimulationTrackerStatus.GeneratingResults]: 'in-progress',
      [SimulationTrackerStatus.Failed]: 'done',
      [SimulationTrackerStatus.Completed]: 'success',
    };

  /**
   * Maps simulation statuses to their corresponding categories.
   * @param status The simulation status.
   * @returns The corresponding category string.
   */
  static GetCategory(status: SimulationTrackerStatus): StatusCategory {
    return this._categoryMap[status]!;
  }

  /**
   * Checks if the simulation is in progress.
   * @param status The simulation status.
   * @returns True if in progress, false otherwise.
   */
  static IsInProgress(status: SimulationTrackerStatus): boolean {
    return this.GetCategory(status) === 'in-progress';
  }

  /**
   * Checks if the simulation is done.
   * @param status The simulation status.
   * @returns True if done, false otherwise.
   */
  static IsDone(status: SimulationTrackerStatus): boolean {
    return this.GetCategory(status) !== 'in-progress';
  }

  /**
   * Checks if the simulation is complete.
   * @param status The simulation status.
   * @returns True if complete, false otherwise.
   */
  static IsSuccessful(status: SimulationTrackerStatus): boolean {
    return this.GetCategory(status) === 'success';
  }

  static IsFailed(status: SimulationTrackerStatus): boolean {
    return this.GetCategory(status) === 'failed';
  }


  getRouteForSimulationTrackerViewing(
    simulationTracker: SimulationTrackerIds
  ): string[] {
    return [
      '/predictor',
      simulationTracker.studyAreaId,
      'simulation-results',
      simulationTracker.simulationTrackerId ?? 'no-simulation-id',
    ];
  }
}
